import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { adminReportContentQuery } from "@/lib/query-registry";
import { InternalPreviewBanner } from "@/components/admin/InternalPreviewBanner";
import { ScoreGauge } from "@/components/geo/ScoreGauge";
import { DimensionBars } from "@/components/geo/DimensionBars";
import { RoadmapTimeline } from "@/components/geo/RoadmapTimeline";
import { BenchmarkBars } from "@/components/geo/BenchmarkBars";

export const Route = createFileRoute("/admin/orders/$id/report-preview")({
  head: () => ({
    meta: [
      { title: "Report preview (internal) · Shugert Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReportPreviewPage,
});

type Section = {
  key?: string;
  label?: string;
  score?: number;
  max_score?: number;
  rating?: string;
  narrative?: string;
  recommendations?: string[];
  raw_data?: unknown;
};

type ScorecardRow = {
  status?: string;
  key?: string;
  label?: string;
  score?: number;
  max_score?: number;
  rating?: string;
  priority?: string;
};

type Phase = {
  name?: string;
  day_range?: string;
  blocks?: Array<{ heading?: string; body?: string }>;
};

function ReportPreviewPage() {
  const { id } = Route.useParams();
  const q = useQuery(adminReportContentQuery(id));
  const report = q.data?.report as
    | { content: Record<string, unknown> | null; exists: boolean }
    | undefined;

  if (q.isPending) {
    return <p className="text-sm text-muted-foreground">Loading report…</p>;
  }
  if (q.isError) {
    return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  }
  if (!report?.exists || !report.content) {
    return (
      <div>
        <InternalPreviewBanner variant="admin-report-preview" />
        <p className="text-sm text-muted-foreground">
          No structured report stored for this order yet.
        </p>
      </div>
    );
  }

  const content = report.content as Record<string, unknown>;
  const meta = (content.meta as Record<string, unknown>) ?? {};
  const overall = (content.overall as { score?: number; rating?: string; priority?: string }) ?? {};
  const scorecard = (content.scorecard as ScorecardRow[]) ?? [];
  const sections = (content.sections as Section[]) ?? [];
  const roadmap = (content.roadmap as { philosophy?: string; phases?: Phase[] }) ?? { phases: [] };

  const timelinePhases = (roadmap.phases ?? []).map((p) => ({
    label: `${p.name ?? ""} · ${p.day_range ?? ""}`.replace(/^ · | · $/g, ""),
    actions: (p.blocks ?? []).map((b) => ({
      title: b.heading ?? "",
      impact: b.body ?? "",
      priority: "Medium",
    })),
  }));

  return (
    <>
      {/* Print styles scoped to this page. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @page { size: Letter; margin: 0.75in; }
            @media print {
              body { background: white !important; }
              .no-print { display: none !important; }
              [data-internal-preview-banner] { display: block !important; }
            }
            .report-preview-body {
              max-width: 8in;
              margin: 0 auto;
              font-family: "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              color: #111;
            }
            .report-preview-body h1, .report-preview-body h2, .report-preview-body h3 {
              font-family: "Sora", "Manrope", sans-serif;
              color: #111;
            }
            .report-preview-body h1 { font-size: 28px; margin: 24px 0 12px; font-weight: 700; }
            .report-preview-body h2 { font-size: 20px; margin: 20px 0 10px; font-weight: 600; }
            .report-preview-body h3 { font-size: 16px; margin: 14px 0 8px; font-weight: 600; }
            .report-preview-body p { margin: 0 0 12px; line-height: 1.6; font-size: 14px; }
          `,
        }}
      />

      <div className="mb-4 flex items-center justify-between gap-3 no-print">
        <a
          href={`/admin/orders/${id}`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          ← Back to order
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background hover:opacity-90"
        >
          Print / Save as PDF
        </button>
      </div>

      <div className="report-preview-body">
        <InternalPreviewBanner variant="admin-report-preview" />

        {/* Header */}
        <header className="border-b border-black/10 pb-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/60">
            GEO Readiness Audit
          </p>
          <h1 className="mt-2">{String(meta.brand ?? "Audit report")}</h1>
          <p className="text-sm text-black/60">
            {String(meta.site ?? "")}
            {meta.report_date ? ` · ${meta.report_date}` : ""}
          </p>
        </header>

        {/* 1.1, Overall verdict with gauge */}
        <section className="mt-8">
          <h2>1.1 Overall verdict</h2>
          <div className="flex flex-wrap items-center gap-8">
            <ScoreGauge
              score={Number(overall.score ?? 0)}
              maxScore={100}
              rating={String(overall.rating ?? "PENDING")}
            />
            <div className="flex-1 min-w-[240px]">
              {typeof content.executive_summary === "string" && content.executive_summary ? (
                <p>{content.executive_summary.split("\n\n")[0]}</p>
              ) : (
                <p className="text-black/50">No executive summary saved.</p>
              )}
            </div>
          </div>
        </section>

        {/* 1.2, Scorecard as dimension bars */}
        <section className="mt-10">
          <h2>1.2 Scorecard summary</h2>
          {scorecard.length > 0 ? (
            <DimensionBars
              dimensions={scorecard
                // v2 rows that were not measured carry no score: leave them out.
                .filter((r) => !r.status || r.status === "measured")
                .map((r) => ({
                  name: r.label ?? r.key ?? ", ",
                  score: Number(r.score ?? 0),
                  maxScore: Number(r.max_score ?? 0),
                  rating: r.rating,
                }))}
            />
          ) : (
            <p className="text-black/50">No scorecard saved.</p>
          )}
        </section>

        {/* 1.4, Biggest opportunity + roadmap */}
        {content.biggest_opportunity ? (
          <section className="mt-10">
            <h2>1.4 Biggest opportunity</h2>
            <p>{String(content.biggest_opportunity)}</p>
          </section>
        ) : null}

        {timelinePhases.length > 0 ? (
          <section className="mt-8">
            <h3>90-day roadmap</h3>
            <RoadmapTimeline phases={timelinePhases} />
          </section>
        ) : null}

        {/* Sections 2..N */}
        {sections.map((s, idx) => {
          // TODO(GEO-PIPELINE-BENCHMARK): opt-in benchmark visualization when
          // the pipeline emits section.raw_data.benchmark = { metric, entries }.
          const benchmark = extractBenchmark(s.raw_data);
          return (
            <section key={s.key ?? idx} className="mt-10 print:break-before-page">
              <h2>
                Section {idx + 2}, {s.label}
              </h2>
              <p className="text-sm text-black/60">
                Score {s.score ?? 0} / {s.max_score ?? 0} · {s.rating ?? ""}
              </p>
              {s.narrative ? (
                <div className="mt-3">
                  {s.narrative.split(/\n\s*\n+/).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              ) : null}
              {benchmark ? (
                <BenchmarkBars
                  metric={benchmark.metric}
                  entries={benchmark.entries}
                  unit={benchmark.unit}
                />
              ) : null}
              {s.recommendations && s.recommendations.length > 0 ? (
                <>
                  <h3>Recommendations</h3>
                  <ul className="ml-4 list-disc">
                    {s.recommendations.map((r, i) => (
                      <li key={i} className="mb-1 text-sm">
                        {r}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          );
        })}
      </div>
    </>
  );
}

function extractBenchmark(raw: unknown): {
  metric: string;
  entries: Array<{ name: string; value: number; isTarget?: boolean }>;
  unit?: string;
} | null {
  if (!raw || typeof raw !== "object") return null;
  const b = (raw as Record<string, unknown>).benchmark;
  if (!b || typeof b !== "object") return null;
  const bb = b as Record<string, unknown>;
  const metric = typeof bb.metric === "string" ? bb.metric : null;
  const entries = Array.isArray(bb.entries) ? bb.entries : null;
  if (!metric || !entries) return null;
  return {
    metric,
    unit: typeof bb.unit === "string" ? bb.unit : undefined,
    entries: entries.flatMap((e) => {
      if (!e || typeof e !== "object") return [];
      const ee = e as Record<string, unknown>;
      const name = typeof ee.name === "string" ? ee.name : null;
      const value = typeof ee.value === "number" ? ee.value : null;
      if (!name || value == null) return [];
      return [{ name, value, isTarget: ee.isTarget === true }];
    }),
  };
}
